import type { FC } from 'react';
import React, { useRef, useState, useEffect } from 'react';
import { cssMap } from '@compiled/react';
import { graphql } from 'react-relay';

import { token } from '@atlaskit/tokens';

import { EditorPageCoverPictureEntry } from '@confluence/page-cover-picture/entry-points/EditorPageCoverPictureEntry.ts';
import { MIN_DYNAMIC_COVER_IMAGE_HEIGHT } from '@confluence/page-cover-picture/entry-points/pageCoverPictureConstants.ts';
import {
	getRendererCoverPictureInfo,
	getDynamicCoverImageHeight,
	propertyToId,
} from '@confluence/page-cover-picture/entry-points/coverPictureHelperFunctions.ts';
import { CoverPictureIdPropertyKey } from '@confluence/page-cover-picture/entry-points/pageCoverPictureTypes.ts';
import { RendererPageCoverPicture } from '@confluence/page-cover-picture/entry-points/RendererPageCoverPicture.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	SmartSpaceOverviewCoverPictureQuery$data,
	SmartSpaceOverviewCoverPictureQuery$variables,
} from './__generated__/SmartSpaceOverviewCoverPictureQuery.graphql.ts';

const SmartSpaceOverviewCoverPictureQuery = graphql`
	query SmartSpaceOverviewCoverPictureQuery(
		$contentId: ID
		$status: [String]
		$draftShareId: String
	) {
		content(id: $contentId, draftShareId: $draftShareId, status: $status) {
			nodes {
				id
				properties(keys: ["cover-picture-id-draft", "cover-picture-id-published"]) {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
			}
		}
	}
`;

const styles = cssMap({
	root: {
		position: 'relative',
		containerType: 'inline-size',
	},
	placeholder: {
		position: 'absolute',
		inset: '0',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		backgroundImage: `linear-gradient(135deg, ${token('color.background.accent.blue.subtlest')} 0%, ${token('color.background.accent.purple.subtlest')} 100%)`,
		borderRadius: token('radius.xlarge'),
	},
	placeholderContent: {
		opacity: 0,
		transition: 'opacity 200ms ease-in-out',
	},
	placeholderContentVisible: {
		opacity: 1,
	},
});

type SmartSpaceOverviewCoverPictureProps = {
	contentId: string;
	isAdmin: boolean;
	hasEmoji: boolean;
};

export const SmartSpaceOverviewCoverPicture: FC<SmartSpaceOverviewCoverPictureProps> = ({
	contentId,
	isAdmin,
	hasEmoji,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [dynamicCoverImageHeight, setDynamicCoverImageHeight] = useState(0);
	const [isHovered, setIsHovered] = useState(false);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const observer = new ResizeObserver(([entry]) => {
			setDynamicCoverImageHeight(getDynamicCoverImageHeight(Math.floor(entry.contentRect.width)));
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const { data } = useQueryInterop<
		SmartSpaceOverviewCoverPictureQuery$data,
		SmartSpaceOverviewCoverPictureQuery$variables
	>(SmartSpaceOverviewCoverPictureQuery, {
		variables: {
			contentId,
			status: ['current', 'draft'],
		},
	});

	const {
		coverPicture,
		contentAppearance,
		hasCoverPicture: hasPublishedCoverPicture,
	} = getRendererCoverPictureInfo(data, false);
	const properties = data?.content?.nodes?.[0]?.properties?.nodes || [];
	const draftCoverPicture = propertyToId(
		properties.find((property) => property?.key === CoverPictureIdPropertyKey.DRAFT),
	);
	const hasDraftCoverPicture = !!draftCoverPicture?.value?.id;
	const hasCoverPicture = hasDraftCoverPicture || hasPublishedCoverPicture;
	const content = isAdmin ? (
		<EditorPageCoverPictureEntry
			key={`${contentId}-${hasCoverPicture ? 'filled' : 'empty'}`}
			contentId={contentId}
			isPublishingContent={false}
			contentType="page"
			userOffline={false}
			hasCoverPicture={hasCoverPicture}
			contentPropertyKey={
				hasDraftCoverPicture ? CoverPictureIdPropertyKey.DRAFT : CoverPictureIdPropertyKey.PUBLISHED
			}
			isSmartSpaceOverview
			isUnpublishedDraft={false}
			isReadOnly={false}
			dynamicCoverImageHeight={dynamicCoverImageHeight}
		/>
	) : hasPublishedCoverPicture ? (
		<RendererPageCoverPicture
			contentId={contentId}
			isPagePreview={false}
			coverPicture={coverPicture}
			contentAppearance={contentAppearance}
			isSpaceOverview
			hasEmoji={hasEmoji}
		/>
	) : null;

	const effectiveHeight =
		dynamicCoverImageHeight > 0 ? dynamicCoverImageHeight : MIN_DYNAMIC_COVER_IMAGE_HEIGHT;

	if (!hasCoverPicture) {
		return (
			<div
				css={styles.root}
				ref={containerRef}
				style={{ minHeight: `${effectiveHeight}px` }}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				onFocus={() => setIsHovered(true)}
				onBlur={() => setIsHovered(false)}
			>
				<div css={styles.placeholder} data-testid="space-overview-cover-placeholder">
					{content && (
						<div css={[styles.placeholderContent, isHovered && styles.placeholderContentVisible]}>
							{content}
						</div>
					)}
				</div>
			</div>
		);
	}

	return (
		<div css={styles.root} ref={containerRef} style={{ minHeight: `${effectiveHeight}px` }}>
			{content}
		</div>
	);
};
